import { CSSProperties } from "react";
import { colors } from "../../../data/colors";
import { pxPerInch as PX_PER_INCH } from "../../../data/stationery";

export const rulerSize = 16;
const tickColor = colors.darkGrey;

const inchLabels = (length: number) =>
  Array.from({ length: Math.floor(length / PX_PER_INCH) }, (_, i) => i + 1);

// Sits outside the page's top and left edge, so tick 0 is at the page corner.
// It lives inside the zoomed page div, so sizes that must stay constant on
// screen (thickness, ticks height, text) are divided by the zoom.
export default function PageRuler({
  width,
  height,
  scale,
}: {
  width: number;
  height: number;
  scale: number;
}) {
  const u = (n: number) => n / Math.max(scale, 0.1);
  const size = u(rulerSize);
  const line = `${u(1)}px solid ${tickColor}`;
  const tick = (angle: number) =>
    `linear-gradient(${angle}deg, ${tickColor} ${u(1)}px, transparent ${u(1)}px)`;

  const base: CSSProperties = {
    position: "absolute",
    pointerEvents: "none",
    userSelect: "none",
    boxSizing: "border-box",
    backgroundColor: colors.white,
    color: colors.darkGrey,
    fontSize: u(9),
    lineHeight: 1,
    overflow: "hidden",
    zIndex: 100,
  };

  return (
    <>
      <div
        style={{
          ...base,
          top: -size,
          left: -size,
          width: size,
          height: size,
          borderRight: line,
          borderBottom: line,
        }}
      />
      <div
        style={{
          ...base,
          top: -size,
          left: 0,
          width: "100%",
          height: size,
          borderBottom: line,
          backgroundImage: [tick(90), tick(90), tick(90)].join(", "),
          backgroundSize: `${PX_PER_INCH / 8}px ${u(4)}px, ${PX_PER_INCH / 2}px ${u(8)}px, ${PX_PER_INCH}px 100%`,
          backgroundPosition: "0 100%",
          backgroundRepeat: "repeat-x",
        }}
      >
        {inchLabels(width).map((inch) => (
          <span
            key={inch}
            style={{
              position: "absolute",
              top: u(1),
              left: inch * PX_PER_INCH + u(3),
            }}
          >
            {inch}
          </span>
        ))}
      </div>
      <div
        style={{
          ...base,
          top: 0,
          left: -size,
          width: size,
          height: "100%",
          borderRight: line,
          backgroundImage: [tick(180), tick(180), tick(180)].join(", "),
          backgroundSize: `${u(4)}px ${PX_PER_INCH / 8}px, ${u(8)}px ${PX_PER_INCH / 2}px, 100% ${PX_PER_INCH}px`,
          backgroundPosition: "100% 0",
          backgroundRepeat: "repeat-y",
        }}
      >
        {inchLabels(height).map((inch) => (
          <span
            key={inch}
            style={{
              position: "absolute",
              left: u(1),
              top: inch * PX_PER_INCH + u(3),
            }}
          >
            {inch}
          </span>
        ))}
      </div>
    </>
  );
}
